import { useState, useEffect } from 'react'

interface CountdownTime {
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
}

const COOKIE_NAME = 'price_locked_997'

function getTonightMidnight(): Date {
  // Target = next 00:00 (tomorrow's midnight = end of today)
  const target = new Date()
  target.setHours(24, 0, 0, 0) // sets to tomorrow at 00:00:00
  return target
}

function setCookie(name: string, value: string, days: number) {
  const date = new Date()
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`
}

function getCookie(name: string): string | null {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim()
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length)
  }
  return null
}

export function useDeadlineCountdown(): CountdownTime {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>(() => {
    // Check cookie immediately on mount — if locked, start as expired
    if (typeof document !== 'undefined' && getCookie(COOKIE_NAME)) {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: true }
    }
    return { hours: 0, minutes: 0, seconds: 0, isExpired: false }
  })

  useEffect(() => {
    // If already expired by cookie, lock and stop
    if (getCookie(COOKIE_NAME)) {
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0, isExpired: true })
      return
    }

    const target = getTonightMidnight()

    const update = () => {
      const now = new Date()
      const diff = target.getTime() - now.getTime()

      if (diff <= 0) {
        // Persist the locked state in browser for 30 days
        setCookie(COOKIE_NAME, '1', 30)
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, isExpired: true })
        return
      }

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeLeft({ hours, minutes, seconds, isExpired: false })
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [])

  return timeLeft
}
